import type { ProfitPushState } from "api/types";

export type ProfitPushPresentation = {
  /** Color de Chakra para el punto y el badge. */
  colorScheme: string;
  /** Etiqueta corta; el drawer mide 560 px. */
  label: string;
  /** Qué significa y qué se puede hacer. Va en el tooltip. */
  help: string;
};

/**
 * Presentación de cada estado.
 *
 * El pedido original era de dos colores: verde si `Processed`, amarillo si no.
 * Se abre a cuatro porque con dos, una orden **rechazada** por el ERP se ve
 * igual que una en cola, y el usuario nunca se entera de que tiene que actuar —
 * que es justamente el caso del ejemplo ("No existen almacenes con código:
 * ZPR1."). Verde y amarillo siguen significando lo mismo que se pidió.
 */
export const PROFIT_PUSH_PRESENTATION: Record<
  ProfitPushState,
  ProfitPushPresentation
> = {
  draft: {
    colorScheme: "gray",
    label: "Sin enviar",
    help: "La orden todavía no se cargó en Profit.",
  },
  sending: {
    colorScheme: "blue",
    label: "Enviando",
    help: "Se está enviando a Profit. La orden queda bloqueada mientras tanto.",
  },
  pending: {
    colorScheme: "yellow",
    label: "En cola",
    help: "Profit la recibió y todavía no la procesó.",
  },
  processed: {
    colorScheme: "green",
    label: "Creada en Profit",
    help: "La orden existe en el ERP. Cualquier corrección es manual allá.",
  },
  rejected: {
    colorScheme: "red",
    label: "Rechazada",
    help: "No llegó a crearse. Corregí lo que indica el mensaje y reenviá.",
  },
  failed: {
    colorScheme: "red",
    label: "Falló en el ERP",
    help: "Entró a Profit y falló allá. Editar acá no lo corrige.",
  },
  unconfirmed: {
    colorScheme: "gray",
    label: "Sin confirmar",
    help: "No se pudo confirmar si llegó a crearse. Verificá en el ERP antes de reenviar.",
  },
};

export function profitPushPresentation(
  state: ProfitPushState | string | undefined,
): ProfitPushPresentation {
  return (
    PROFIT_PUSH_PRESENTATION[state as ProfitPushState] ??
    PROFIT_PUSH_PRESENTATION.draft
  );
}

/** Estados que el seguimiento todavía puede mover. Mientras haya uno, se polea. */
export function isOpenProfitState(
  state: ProfitPushState | string | undefined,
): boolean {
  return state === "sending" || state === "pending";
}

/** Si el botón "Cargar en Profit" tiene sentido en ese estado. */
export function canPushToProfit(
  state: ProfitPushState | string | undefined,
): boolean {
  return state === undefined || state === "draft" || state === "rejected";
}
